import { useEffect, useRef, useState } from "react";
import { ChevronsDown } from "lucide-react";
import { cn } from "src/lib/utils";
import { useLogging } from "src/v1/features/PreData/hooks/useLogging";
import { useKakaoModal } from "src/hooks/useKakaoModal";
import { useTossPixelLead } from "src/hooks/useAds";
import { isPrerender } from "src/common/prerender";
import { CountSkeleton } from "../CountSkeleton";

const BANNER_BASE = "https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/banner-videos";
const DESKTOP_QUERY = "(min-width: 1440px)";
// Posters are served from our own origin (public/) so the prerendered page paints the LCP image
// before the app bundle finishes downloading; same files as on S3.
const POSTER_DESKTOP = "/poster-2026-03-18.webp";
const POSTER_MOBILE = "/poster-mobile-2026-03-18.webp";

// Skip the background video for users who asked for less motion or less data
const shouldSkipVideo = () => {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return (
    isPrerender() ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    connection?.saveData === true
  );
};

// Start the video only after the page has loaded and the main thread is idle,
// so the multi-MB file does not compete with the poster image and JS on first paint.
const useDeferredVideoPlay = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (shouldSkipVideo()) return;

    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const play = () => {
      const video = videoRef.current;
      if (cancelled || !video) return;
      // Browsers only autoplay muted videos. Hydrating the prerendered HTML drops the JSX `muted`
      // (React sets it as a DOM property, so the saved HTML has no muted attribute), so set it here too.
      video.muted = true;
      video.play().catch(() => {
        // Autoplay blocked: keep showing the poster
      });
    };
    const scheduleIdle = () => {
      // Safari has no requestIdleCallback
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(play, { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(play, 1000);
      }
    };

    if (document.readyState === "complete") {
      scheduleIdle();
    } else {
      window.addEventListener("load", scheduleIdle, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", scheduleIdle);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return videoRef;
};

interface MainBannerProps {
  suimCount: number | null;
  // 모바일 하단 상담 바(구글 광고 방문자용)가 있으면 true.
  // 바에 카톡상담이 있으니, 모바일에선 20초 버튼 대신 "아래로 내려보세요" 꺽쇠를 보여준다.
  hasBottomBar?: boolean;
}

export const MainBanner = ({ suimCount, hasBottomBar = false }: MainBannerProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useDeferredVideoPlay();
  const { logEvent } = useLogging();
  const { onClickHandler } = useKakaoModal();
  const trackLead = useTossPixelLead();

  const handle20SecondClick = async () => {
    onClickHandler();
    trackLead("Consultation");
    await logEvent({
      name: "20second_button",
      value: "20초 상담하기",
      type: "button_click",
    });
  };

  return (
    <section className="px-4 pb-4 tablet:px-8 tablet:pb-8 desktop:px-6 desktop:pb-6 h-[calc(100dvh-var(--header-h,64px))] flex flex-col">
      <div className="w-full flex-1 p-6 tablet:p-8 desktop:p-0 rounded-[24px] relative overflow-hidden flex desktop:items-center desktop:justify-center">
        {/* Video Poster - 영상 로딩 전까지 표시 */}
        {!isVideoPlaying && (
          <picture className="absolute inset-0 z-[1]">
            <source
              srcSet={POSTER_DESKTOP}
              media={DESKTOP_QUERY}
            />
            <img
              src={POSTER_MOBILE}
              alt=""
              fetchPriority="high"
              className="size-full object-cover"
            />
          </picture>
        )}

        {/* Background Video — no download until useDeferredVideoPlay starts it after page load */}
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          onPlaying={() => setIsVideoPlaying(true)}
          className="absolute inset-0 size-full object-cover z-0"
        >
          <source
            src={`${BANNER_BASE}/2026-06-16-2.mp4`}
            type="video/mp4"
            media={DESKTOP_QUERY}
          />
          <source
            src={`${BANNER_BASE}/mobile-2026-06-16-2.mp4`}
            type="video/mp4"
            media="(max-width: 1439px)"
          />
        </video>

        <div className="w-full max-w-[1150px] mx-auto flex flex-col desktop:gap-10 relative z-10 items-center justify-between desktop:justify-center desktop:items-start">
          <div className="flex flex-col gap-7">
            <h1 className="text-white text-heading-2 tablet:text-heading-1 text-center desktop:text-left">
              <div>
                소송, 강제집행까지 <br className="desktop:hidden" />
                <span className="">10만원부터</span>
              </div>
              <div>확실한 내 돈 찾기</div>
            </h1>
            <div className="hidden desktop:block text-heading-3 !font-normal text-white">
              비용 없는 법률상담, 든든한 변호사 수임료 지원
            </div>
          </div>

          <div className="flex flex-col items-center">
            {/* 하단 바가 있으면 모바일에서 문구를 16px 로 키우고, 한 줄에 들어가도록 '변호사'를 뺀다 (시안 문구).
                (여기선 cn 을 쓰지 않는다: cn 이 text-body-* 를 글자색으로 착각해 text-white 를 지운다) */}
            <div
              className={`desktop:hidden ${hasBottomBar ? "text-body-1-mb tablet:text-body-2-mb" : "text-body-2-mb"} !font-normal text-white mb-3 text-center`}
            >
              비용 없는 법률상담, 든든한{" "}
              <span className={hasBottomBar ? "hidden tablet:inline" : undefined}>변호사 </span>
              수임료 지원
            </div>
            {hasBottomBar && (
              <ChevronsDown
                aria-hidden
                className="size-[24px] text-white animate-bob motion-reduce:animate-none tablet:hidden"
              />
            )}
            {/* 버튼 — 하단 바가 있으면 모바일에선 숨긴다 */}
            <button
              className={cn(
                "w-[160px] tablet:w-[200px] rounded-[24px] bg-[#F6BE28] py-[12px] px-[38px] tablet:px-[48px] tablet:py-[14px] cursor-pointer text-center transition-all duration-300 hover:brightness-110 text-[14px] font-bold tablet:text-button-1 text-white tablet:mb-14 desktop:mb-0 whitespace-nowrap",
                hasBottomBar && "hidden tablet:block",
              )}
              onClick={handle20SecondClick}
              id="kakao_click"
            >
              20초 상담하기
            </button>
          </div>
        </div>
      </div>
      {/* 통계 — 모바일에서 이 영역(mt-3 + h-14 + 섹션 pb-4 = 84px)을 구글 광고 상담 바가 덮는다. 크기를 바꾸면 LandingPage 의 --lead-bar-h 도 같이 바꾼다 */}
      {/* 태블릿부터 통계와 모서리 이미지(masking)는 배너 위에 뜬다 → tablet:z-[2] 로 포스터(z-[1]) 위에 둔다. 영상이 재생되지 않아도 보이게 */}
      <div className="w-full h-14 mt-3 px-8 tablet:mt-0 tablet:absolute tablet:z-[2] tablet:w-[456px] desktop:w-[924px] tablet:bottom-7 tablet:right-8 tablet:px-8 tablet:py-2 desktop:bottom-4 desktop:right-6 tablet:h-[72px] desktop:h-[111px] bg-white rounded-tl-[24px] desktop:px-32 desktop:py-4 flex items-center justify-center">
        <div className="w-full flex justify-between items-center">
          <div className="text-center">
            <div className="text-body-2-mb tablet:text-heading-2-mb desktop:text-heading-2">
              수임건수
            </div>
            <div className="text-heading-3-mb tablet:text-heading-1-mb desktop:text-heading-1 flex items-center justify-center gap-1">
              <div className="text-c-primary">
                {suimCount === null ? <CountSkeleton /> : suimCount.toLocaleString()}
              </div>
              <div className="text-[#3C3E4F]"> +</div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-body-2-mb tablet:text-heading-2-mb desktop:text-heading-2">
              수임료 최대지원금
            </div>
            <div className="text-heading-3-mb tablet:text-heading-1-mb text-c-primary desktop:text-heading-1">
              1,350
              <span className="text-[#3C3E4F]"> 만 원</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-body-2-mb tablet:text-heading-2-mb desktop:text-heading-2">
              회수율
            </div>
            <div className="text-heading-3-mb tablet:text-heading-1-mb text-c-primary desktop:text-heading-1">
              79
              <span className="text-[#3C3E4F]"> %</span>
            </div>
          </div>
        </div>
      </div>
      <img
        src="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/image-fill.webp"
        alt="masking"
        className="hidden tablet:block tablet:absolute tablet:z-[2] tablet:bottom-[93px] tablet:right-[31px] desktop:block desktop:absolute desktop:bottom-[120px] desktop:right-[23px]"
      />
      <img
        src="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/image-fill.webp"
        alt="masking"
        className="hidden tablet:block tablet:absolute tablet:z-[2] tablet:bottom-[12px] tablet:right-[487px] desktop:block desktop:absolute desktop:bottom-[17px] desktop:right-[947px]"
      />
    </section>
  );
};
